import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BellRing, X, Crown } from "lucide-react";

const SHOPIER_LINK = "https://www.shopier.com/noktaders/50725616";
const SHOWN_KEY = "nd_shown_notifs";

function daysUntil(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

export default function RenewalReminder() {
  const { user } = useAuth();
  const dismissKey = `nd_renewal_dismissed_${user?.subscription_until || ""}`;
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(dismissKey) === "1");

  // In-app notifications (e.g. admin granted a 30-day subscription) shown as toasts.
  useEffect(() => {
    if (!user || user.role === "admin") return;
    let active = true;
    api.get("/notifications").then(({ data }) => {
      if (!active) return;
      const shown = new Set(JSON.parse(sessionStorage.getItem(SHOWN_KEY) || "[]"));
      (data || []).filter((n) => !n.read && !shown.has(n.id)).forEach((n) => {
        toast(n.text, { duration: 8000 });
        shown.add(n.id);
        api.post(`/notifications/${n.id}/read`).catch(() => {});
      });
      sessionStorage.setItem(SHOWN_KEY, JSON.stringify([...shown]));
    }).catch(() => {});
    return () => { active = false; };
  }, [user]);

  if (!user || user.role === "admin") return null;

  const paid = user.subscription_type === "paid";
  const daysLeft = daysUntil(user.subscription_until);
  const active = user.is_subscribed;

  const endingSoon = active && paid && daysLeft !== null && daysLeft <= 2 && daysLeft >= 0;
  const ended = !active && paid && !!user.subscription_until && daysLeft !== null && daysLeft < 0;

  if (dismissed || (!endingSoon && !ended)) return null;

  const close = () => {
    sessionStorage.setItem(dismissKey, "1");
    setDismissed(true);
  };
  const extend = () => window.open(SHOPIER_LINK, "_blank", "noopener,noreferrer");

  let title;
  let message;
  if (ended) {
    title = "Aboneliğin sona erdi";
    message = "Aboneliğinin kullanım süresi doldu. Kaldığın yerden devam etmek için aboneliğini uzat.";
  } else if (daysLeft === 2) {
    title = "Hatırlatma!";
    message = "Aboneliğinizin 30 günlük kullanım süresi iki gün sonra sona erecektir.";
  } else if (daysLeft === 1) {
    title = "Hatırlatma!";
    message = "Aboneliğinizin kullanım süresi yarın sona erecektir.";
  } else {
    title = "Hatırlatma!";
    message = "Aboneliğinizin kullanım süresi bugün sona eriyor.";
  }

  return (
    <div
      data-testid="renewal-reminder"
      className="fixed bottom-4 right-4 z-[60] w-[320px] max-w-[calc(100vw-2rem)] animate-float-up"
    >
      <div className="relative bg-white border-2 border-amber-300 rounded-2xl shadow-2xl p-4">
        <span className="absolute -bottom-2 right-8 w-4 h-4 bg-white border-b-2 border-r-2 border-amber-300 rotate-45" />
        <button
          onClick={close}
          data-testid="renewal-reminder-close"
          aria-label="Kapat"
          className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-700 rounded-lg"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 pr-5">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
            <BellRing className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-slate-900 text-sm">{title}</p>
            <p className="text-sm text-slate-600 mt-0.5 leading-snug">{message}</p>
          </div>
        </div>
        <button
          onClick={extend}
          data-testid="renewal-extend-btn"
          className="mt-3 w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm py-2.5 rounded-xl transition-colors"
        >
          <Crown className="w-4 h-4" /> Aboneliği Uzat
        </button>
      </div>
    </div>
  );
}
